import { FormPagination } from "components/form";
import { Overlay, PageContent, PageHeader, Tab, Tabs } from "components/layout";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { useCreateManifest, useUpdateManifest } from "hooks/manifests";
import { useRouter } from "hooks/utils";
import { trans } from "src/translations";
import { pathToUrl } from "utils/url";

import { PATHS } from "../constants";
import { ManifestData } from "../ManifestData";
import { getDetailsPath } from "../utils";
import { AppManifestTargetTab } from "./AppManifestTargetTab";
import { ManifestEditFormik, ManifestEditTabs } from "./components";
import { TABS } from "./constants";

const createTabLink = ({ id, tabId }) => {
  return pathToUrl(id ? PATHS.edit : PATHS.create, {
    id: encodeURIComponent(id),
    tabId,
  });
};

const isTabValid = (fields, errors) => {
  return fields.every((fieldName) => !errors[fieldName]);
};

export function AppManifestForm() {
  const { params, navigate } = useRouter();
  const { id, tabId } = params;
  const createManifest = useCreateManifest();
  const updateManifest = useUpdateManifest(id);

  const getActiveTab = () => TABS.findIndex(({ path }) => path === tabId);

  const getTabs = (errors) =>
    TABS.map((tab, idx) => {
      const isValid = isTabValid(tab.validateFields, errors);
      return (
        <Tab
          key={tab.path}
          active={getActiveTab() === idx}
          text={tab.text}
          icon={isValid ? "ok" : "warning"}
          iconColor={isValid ? "success" : "error"}
          dataTestId={tab.dataTestId}
          onClick={() => {
            navigate(createTabLink({ id, tabId: tab.path }));
          }}
        />
      );
    });

  const handleNav = (num) => {
    const nextPage = TABS[getActiveTab() + num];
    return createTabLink({ id, tabId: nextPage.path });
  };

  const renderTabs = (handleSubmit, handleReset, isSubmitting) => {
    return (
      <Overlay active={isSubmitting} message={trans.SUBMITTING()}>
        {getActiveTab() === 0 && <AppManifestTargetTab />}
        {getActiveTab() === 1 && (
          <ManifestEditTabs
            createManifest={createManifest}
            updateManifest={updateManifest}
          />
        )}
        <FormPagination
          activeTab={getActiveTab()}
          discardLabel={trans.APP_DETAILS_MANIFEST__EDIT_DISCARD()}
          saving={createManifest.isPending || updateManifest.isPending}
          tabCount={TABS.length}
          tabTitle={TABS.map((t) => t.text)[getActiveTab()]}
          onDiscard={handleReset}
          onNext={() => handleNav(1)}
          onPrev={() => handleNav(-1)}
          onSubmit={handleSubmit}
        />
      </Overlay>
    );
  };

  return (
    <>
      <PageHeader>{trans.APP_MANIFESTS_EDIT_PAGE_HEADER()}</PageHeader>
      <PageContent>
        <RestrictedArea allowed={[ROLES.moderator.appIntegrator]}>
          <ManifestData id={id}>
            <ManifestEditFormik
              createUpdateManifest={id ? updateManifest : createManifest}
              onSuccess={(manifestId) => navigate(getDetailsPath(manifestId))}
            >
              {({ handleSubmit, handleReset, isSubmitting, errors }) => (
                <>
                  <Tabs>{getTabs(errors)}</Tabs>
                  {renderTabs(handleSubmit, handleReset, isSubmitting)}
                </>
              )}
            </ManifestEditFormik>
          </ManifestData>
        </RestrictedArea>
      </PageContent>
    </>
  );
}
